/**
 * NIZAM ONE ERP — Module Context (Batch 4 / PG-007)
 * Provides company-specific module enablement state to the entire app.
 * Reloads when activeCompanyId changes.
 */
import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useCompany } from './CompanyContext';
import type { ModuleStatus } from '../lib/queries/moduleActivation';
import { loadCompanyModules, isModuleEnabled as checkEnabled, clearModuleCache } from '../lib/modules/moduleService';

// ── Types ──────────────────────────────────────────────────────────────────────

interface ModuleContextType {
  /** All module statuses for the active company */
  modules: ModuleStatus[];
  /** Set of enabled module_keys for the active company */
  enabledModules: Set<string>;
  /** Whether module data is still loading */
  loadingModules: boolean;
  /** Returns true if the module is effectively enabled (core = always true) */
  isModuleEnabled: (moduleKey: string) => boolean;
  /** Force refresh from DB (call after enable/disable mutations) */
  refreshModules: () => Promise<void>;
}

const ModuleContext = createContext<ModuleContextType | null>(null);

export function useModuleContext(): ModuleContextType {
  const ctx = useContext(ModuleContext);
  if (!ctx) throw new Error('useModuleContext must be used within ModuleProvider');
  return ctx;
}

// ── Provider ───────────────────────────────────────────────────────────────────

export function ModuleProvider({ children }: { children: ReactNode }) {
  const { activeCompanyId } = useCompany();
  const [modules, setModules] = useState<ModuleStatus[]>([]);
  const [loadingModules, setLoadingModules] = useState(true);

  const fetchModules = useCallback(async () => {
    if (!activeCompanyId) {
      setModules([]);
      setLoadingModules(false);
      return;
    }
    setLoadingModules(true);
    try {
      const data = await loadCompanyModules(activeCompanyId);
      setModules(data);
    } catch {
      setModules([]);
    } finally {
      setLoadingModules(false);
    }
  }, [activeCompanyId]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const refreshModules = useCallback(async () => {
    if (activeCompanyId) clearModuleCache(activeCompanyId);
    await fetchModules();
  }, [activeCompanyId, fetchModules]);

  const enabledModules = new Set<string>(
    modules.filter(m => m.effective_enabled).map(m => m.module_key)
  );

  const isModuleEnabled = useCallback(
    (moduleKey: string) => checkEnabled(moduleKey, modules),
    [modules]
  );

  return (
    <ModuleContext.Provider value={{ modules, enabledModules, loadingModules, isModuleEnabled, refreshModules }}>
      {children}
    </ModuleContext.Provider>
  );
}
